import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button, Dropdown, Modal } from 'flowbite-react';
import MockupCreator from './MockupCreator';
import formConfig from './formConfig.json';
import CustomSignupForm from './components/CustomSignupForm';

const safeResizeObserver = (callback) => {
  return new ResizeObserver((entries) => {
    window.requestAnimationFrame(() => {
      if (!Array.isArray(entries) || !entries.length) {
        return;
      }
      callback(entries);
    });
  });
};

const useMockupImages = (clientMockups) => {
    const [mockupImages, setMockupImages] = useState([]);
    
    useEffect(() => {
        const fetchMockupImages = async () => {
        //    console.log('Fetching mockup images for client:', clientMockups);
            let data;

            if (process.env.NODE_ENV === "development") {
                let importedData;
                switch(clientMockups) {
                    case 'giftpack':
                        importedData = await import('./mockupImagesGiftPack.json');
                        break;
                    case 'amrod':
                        importedData = await import('./mockupImagesAmrod.json');
                        break;
                        case 'box':
                          importedData = await import('./mockupImagesBox.json');
                          break;
                        case 'examples':
                          importedData = await import('./mockupImagesExamples.json');
                          break;    
                    case 'merchdize':
                        importedData = await import('./mockupImagesMerchdize.json');
                        break;
                    case 'zedge':
                        importedData = await import('./mockupImagesZedge.json');
                        break;    
                        case 'royal':
                          importedData = await import('./mockupImagesRoyal.json');
                          break;    
                    case 'zedgeNoContour':
                        importedData = await import('./mockupImagesZedgeNoContour.json');
                        break;
                    default:
                        importedData = await import('./mockupImages.json');
                }
                data = importedData.default;
            } else {
                // Logic for production using fetch from a bucket URL
            //    console.log('env-bucketUrl', process.env.REACT_APP_BUCKET_URL);
                let bucketUrl = process.env.REACT_APP_BUCKET_URL || "https://storage.googleapis.com/mockup-multi-script/instantmockups/";
                switch(clientMockups) {
                    case 'giftpack':
                        bucketUrl += "mockupImagesGiftPack.json";
                        break;
                    case 'amrod':
                        bucketUrl += "mockupImagesAmrod.json";
                        break;
                    case 'box':
                        bucketUrl += "mockupImagesBox.json";
                        break;
                        case 'examples':
                          bucketUrl += "mockupImagesExamples.json";
                          break;    
                    case 'merchdize':
                        bucketUrl += "mockupImagesMerchdize.json";
                        break;
                    case 'zedge':
                        bucketUrl += "mockupImagesZedge.json";
                        break;
                        case 'royal':
                          bucketUrl += "mockupImagesRoyal.json";
                          break;
                    case 'zedgeNoContour':
                        bucketUrl += "mockupImagesZedgeNoContour.json";
                        break;
                    default:
                        bucketUrl += "mockupImages.json";
                }
                const response = await fetch(bucketUrl+ "?v=" + Math.random());
                data = await response.json();
            }
        //    console.log('Fetched mockup images:', data);
            setMockupImages(data);
        };

        fetchMockupImages();
    }, [clientMockups]);

    return mockupImages;
};

// Function to parse the category string into an array
const parseCategoryString = (categoryString) => {
    if (!categoryString) return [];
    try {
      const categories = categoryString.replace(/^\[|\]$/g, '').split(',').map(cat => cat.trim());
      //console.log("Parsed categories:", categories); // Add this line for debugging
      return categories;
    } catch (error) {
      console.error("Error parsing category string:", error);
      return [];
    }
  };

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
};

function MockupImages({ onImageClick, selectedMockup, handleMockupImageSelection, categories, categoriesHide, imageUrls, imageLoading, highQualityImages = {}, clientMockups, updateFilteredImages, inputChanged=null, resetInputChanged=null, clientId = 'default', uploadedImageUrl, onBaseImagesLoaded }) {
    const mockupImages = useMockupImages(clientMockups);
    const [imagesLoaded, setImagesLoaded] = useState(false);
    const defaultCategory = categories ? categories[0] : "All";
    const [selectedCategory, setSelectedCategory] = useState(defaultCategory);
    const [showModal, setShowModal] = useState(false);
    const [formLoaded, setFormLoaded] = useState(false);
    const formContainerRef = useRef(null);
    const [modalSize, setModalSize] = useState({ width: '100%', height: '615px' });
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);

    const handleModalClose = useCallback(() => {
      setShowModal(false);
      setFormLoaded(false);
      setModalSize({ width: '100%', height: '615px' });
    }, []);

    const handleFormSubmit = useCallback(() => {
        setIsFormSubmitted(true);
        handleModalClose();
    }, [handleModalClose]);

    const renderFormContent = () => {
      const formUrl = formConfig[clientId]?.formUrl || formConfig.default.formUrl;
      
      if (formUrl === 'custom') {
        return <CustomSignupForm onFormSubmit={handleFormSubmit} />;
      } else {
        return null; // We'll handle the iframe creation in the useEffect
      }
    };

    useEffect(() => {
        console.log("MockupImages received clientId:", clientId);
        console.log("MockupImages received clientMockups:", clientMockups);
        if (showModal && !formLoaded && formContainerRef.current) {
            console.log("Loading form for clientId:", clientId);
            // Use clientMockups instead of clientId for form configuration
            const formUrl = formConfig[clientMockups]?.formUrl || formConfig.default.formUrl;
            console.log("Form URL:", formUrl);
            
            if (formUrl !== 'custom') {
                // Clear any existing content
                if (formContainerRef.current) {
                    formContainerRef.current.innerHTML = '';
                }
                
                const f = document.createElement("iframe");
                f.src = formUrl;
                f.style.border = "none";
                f.style.height = "615px";
                f.style.width = "100%";
                f.style.transition = "all 0.5s ease";
                f.setAttribute("aria-label", 'Signup Form');

                if (formContainerRef.current) {
                    formContainerRef.current.appendChild(f);
                    console.log("Iframe appended to container");
                    
                    // Set formLoaded to true after appending the iframe
                    setFormLoaded(true);
                }

                const handleMessage = (event) => {
                    console.log("Received message:", event.data);
                    const evntData = event.data;
                    if (evntData && evntData.constructor === String) {
                        const zf_ifrm_data = evntData.split("|");
                        if (zf_ifrm_data.length === 2 || zf_ifrm_data.length === 3) {
                            const zf_perma = zf_ifrm_data[0];
                            const zf_ifrm_ht_nw = (parseInt(zf_ifrm_data[1], 10) + 15) + "px";
                            requestAnimationFrame(() => {
                                setModalSize(prevSize => ({ ...prevSize, height: zf_ifrm_ht_nw }));
                            });
                            if ((f.src).indexOf('formperma') > 0 && (f.src).indexOf(zf_perma) > 0) {
                                f.style.height = zf_ifrm_ht_nw;
                                console.log("Iframe height updated:", zf_ifrm_ht_nw);
                            }
                        }
                    }
                };

                window.addEventListener('message', handleMessage, false);

                const resizeObserver = safeResizeObserver(([entry]) => {
                    setModalSize({ width: '100%', height: `${entry.contentRect.height}px` });
                });

                if (formContainerRef.current) {
                    resizeObserver.observe(formContainerRef.current);
                }

                return () => {
                    window.removeEventListener('message', handleMessage);
                    resizeObserver.disconnect();
                };
            } else {
                setFormLoaded(true);
            }
        }
    }, [showModal, formLoaded, clientId, clientMockups]);

    const handleCategoryChange = (category) => {
        setSelectedCategory(category);
        const newFilteredImages = mockupImages.filter(image => {
          const imageCategories = parseCategoryString(image.category);
          return category === categories[0] || imageCategories.includes(category);
        });
        handleMockupImageSelection(newFilteredImages);
      };
      
  
    // Trigger category change when mockupImages is updated
    useEffect(() => {
        console.log("MockupImages useEffect triggered");
        console.log("mockupImages length:", mockupImages.length);
        if (mockupImages.length > 0 && !imagesLoaded) {
            handleCategoryChange(defaultCategory);
            console.log("Calling onBaseImagesLoaded from useEffect");
            onBaseImagesLoaded();
            setImagesLoaded(true);
        }
    }, [mockupImages, defaultCategory, clientId, onBaseImagesLoaded, imagesLoaded]);

    // Add this new useEffect
    useEffect(() => {
      if (mockupImages.length > 0 && !imagesLoaded) {
        console.log("MockupImages loaded, calling onBaseImagesLoaded immediately");
        onBaseImagesLoaded();
        setImagesLoaded(true);
      }
    }, [mockupImages, imagesLoaded, onBaseImagesLoaded]);

    const hideMenu = formConfig[clientId]?.hideMenu || formConfig.default.hideMenu;

    // Comment out these debug logs
    // console.log('Current clientId:', clientId);
    // console.log('hideMenu value:', hideMenu);
    // console.log('formConfig:', formConfig);

    const [selectedImageId, setSelectedImageId] = useState(null);

    const filteredImages = mockupImages.filter((image) => {
        // Parse the category string into an array
        const imageCategories = parseCategoryString(image.category);
    
        if (!image.category || imageCategories.length === 0) {
            // If there's no category data or it's empty after parsing, filter based on the default or first category
            return selectedCategory === categories[0]; // Assuming the first category (e.g., 'All') should show all items
        } else {
            // Check if the selectedCategory is in the parsed categories array
            return  imageCategories.map(cat => cat.toLowerCase().trim()).includes(selectedCategory.toLowerCase().trim());
        }
    });

    useEffect(() => {
        updateFilteredImages(filteredImages);
    }, [filteredImages, updateFilteredImages]);

    const handleImageClick = (image) => {
        console.log(`input changed: ${inputChanged}`);
        if (!image.locked || inputChanged) { // Check if inputChanged is true before proceeding
            onImageClick(image.url, image.id);
            resetInputChanged(); // Reset inputChanged to false after an image is clicked
            
            // Scroll to top on mobile devices
            if (window.innerWidth <= 768) {  // Adjust this breakpoint as needed
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                });
            }
        }
    };

    const handleSignupClick = (image) => {
        console.log("Signup clicked, showForm:", image.showForm);
        if (image.showForm) {
            scrollToTop();
            setTimeout(() => {
                setShowModal(true);
            }, 300); // Delay opening the modal slightly to allow smooth scrolling
        }
    };

    useEffect(() => {
      if (formContainerRef.current) {
        console.log("Form container content:", formContainerRef.current.innerHTML);
      }
    }, [formLoaded]);

    useEffect(() => {
      if (showModal) {
        scrollToTop();
      }
    }, [showModal]);

    console.log("Categories:", categories);
    console.log("hideMenu:", hideMenu);
    console.log("categoriesHide:", categoriesHide);

    // Get the loadingCallToAction from formConfig, use default if not found
    const loadingCallToAction = formConfig[clientId]?.loadingCallToAction || formConfig.default.loadingCallToAction || "Signup to unlock";

    return (
      
      <div className="text-center container px-0 sm:px-4 shadow-sm mx-auto p-4">
            {!categoriesHide && (
                <div className="mb-4 flex justify-center">
                  {!categoriesHide && (
                    <Dropdown
                      className="text-center px-0"
                      color="light"
                      label={selectedCategory || "Product categories"}
                      dismissOnClick={true}
                    >
                      {categories.map((category, index) => (
                        <Dropdown.Item
                          color="gray"
                          className="pl-0 px-0 catagories-dropdown text-left"
                          key={category}
                          onClick={() => handleCategoryChange(category)}
                        >
                          {category}
                        </Dropdown.Item>
                      ))}
                    </Dropdown>
                  )}
                </div>
            )}

            {/*<Button.Group className="mb-4">
                {categories.map((category, index) => (
                    <Button
                        color="gray"
                        key={category}
                        onClick={() => setSelectedCategory(category)}
                    >
                        {category}
                    </Button>
                ))}
                </Button.Group>*/}

<div className="grid grid-cols-2 shadow-sm md:grid-cols-4 gap-4">
  {filteredImages.map((image) => (
    
    <div key={image.id} className={`grid gap-4 id-${image.id}`} style={{ border: selectedImageId === image.id ? "2px solid blue" : "none", height: "100%", position: "relative" }}>
      {image.locked && !isFormSubmitted && (
        <div 
          className="locked-text cursor-pointer hover:bg-opacity-80 transition-all duration-300 ease-in-out"
          style={{ 
            zIndex: 3,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            borderRadius: '5px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            fontWeight: 'bold',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
            padding: '8px 12px',
            fontSize: 'clamp(12px, 2.5vw, 16px)',
            maxWidth: '90%',
            width: 'auto',
            whiteSpace: 'normal',
            lineHeight: '1.2'
          }}
          onClick={() => handleSignupClick(image)}
        >
          {loadingCallToAction}
        </div>
      )}
      <div className={`shadow-md ${image.locked && !isFormSubmitted ? "locked-image" : ""} rounded-lg hover:shadow-xl transition-shadow duration-300 ease-in-out flex items-center justify-center`} style={{ aspectRatio: 'auto', overflow: 'hidden', position: 'relative' }}>
        <div className={`preview-image-container ${imageLoading[image.id] ? 'loading' : ''}`}>
          <img
            className="base-image h-full w-full object-contain rounded-lg cursor-pointer"
            src={highQualityImages[image.id] || imageUrls[image.id] || image.url}
            alt=""
            onClick={() => handleImageClick(image)}
          />
          {image.urlAfterPreview && !imageLoading[image.id] && (
            <img
              className="preview-image h-full w-full object-contain rounded-lg cursor-pointer"
              src={image.urlAfterPreview}
              alt=""
              onClick={() => handleImageClick(image)}
            />
          )}
        </div>
        {imageLoading[image.id] && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 2,
          }}>
            <MockupCreator
              mockupImageUrl={image.url}
              logoUrl={uploadedImageUrl}
              width="100%"
              height="100%"
              clientId={clientId}
            />
          </div>
        )}
        {/* High quality loading indicator */}
        {imageUrls[image.id] && !imageLoading[image.id] && !highQualityImages[image.id] && (
          <div style={{
            position: 'absolute',
            bottom: '8px',
            right: '8px',
            backgroundColor: 'rgba(59, 130, 246, 0.95)',
            color: 'white',
            padding: '6px 10px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '11px',
            fontWeight: '600',
            zIndex: 3,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
          }}>
            <div className="high-quality-spinner" style={{
              width: '12px',
              height: '12px',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              borderTopColor: 'white',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite'
            }} />
            <span>HD ready soon</span>
          </div>
        )}
      </div>
    </div>
  ))}
</div>

<Modal 
    show={showModal} 
    onClose={handleModalClose} 
    size="lg"
    position="center" // Change this to center
    className="modal-custom" // Add a custom class for styling
    dismissible={true}
>
    <Modal.Header className="p-2 border-b">
      {/* You can add a title here if needed */}
    </Modal.Header>
    <Modal.Body>
        {!formLoaded && <div className="text-center">Loading...</div>}
        <div ref={formContainerRef} style={modalSize}>
            {formLoaded && formConfig[clientMockups]?.formUrl === 'custom' && renderFormContent()}
        </div>
    </Modal.Body>
</Modal>
<style>
  {`
    [data-testid="modal"] > div {
      margin-top: ${formConfig[clientId]?.marginTop || 200}px!important;
      transform: none !important;
    }

    .preview-image-container {
      position: relative;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .base-image {
      position: relative;
      width: 100%;
      height: 100%;
      object-fit: contain;
    }

    .preview-image {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: contain;
      animation: fadeInOut 4s ease-in-out infinite;
    }

    @keyframes fadeInOut {
      0% { opacity: 0; }
      50% { opacity: 1; }
      100% { opacity: 0; }
    }

    .loading .preview-image {
      animation: none;
      opacity: 0;
    }

    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `}
</style>
        </div>
        
        
    );
}

export default React.memo(MockupImages);